import type { ComponentRef, EditorSDK, Layout } from '@wix/platform-editor-sdk';

import { APP_TOKEN } from '../../../constants';
import type {
  AnyRecord,
  Font,
  HorizontalMemberMenuMigrationData,
  NewMemberMenuComponentData,
} from './v2-migration-types';
import { convertFontSizeToMobile, parseIntFromPx } from './v2-migration-utils';

type HorizontalMenuStyleParams = {
  properties: AnyRecord;
  propertiesOverride?: {
    fnt?: Partial<Font>;
    fntDrop?: Partial<Font>;
  };
};

type StyleMapperUtilsGetterParams = {
  editorSDK: EditorSDK;
  stCss: string;
  oldSkin: string;
  oldStyle: HorizontalMenuStyleParams;
  oldLayout: Layout;
  isMobile: boolean;
};

type HorizontalMenuMigrationParams = {
  editorSDK: EditorSDK;
  migrationData: HorizontalMemberMenuMigrationData;
  isMobile: boolean;
};

type StyleMapperUtils = Awaited<ReturnType<typeof getStyleMapperUtils>>;

const defaultStCssDesktop = `:import{-st-from:'wix-ui-santa/index.st.css';-st-named:StylableHorizontalMenu}.root{-st-extends:StylableHorizontalMenu;padding:2px;padding-bottom:0;padding-right:0;padding-left:0;padding-top:0;border:0 solid #000;border-bottom:1px solid rgba(24,24,24,0.3);background:value(site_1_1)}.root::menu{direction:ltr;justify-content:flex-start;flex-grow:unset;height:calc(100%+0);margin-top:-0;margin-bottom:-0;width:calc(100%+0);margin-left:-0;margin-right:-0}.root::menuItem{margin-top:0;margin-bottom:0;border:0 solid #757575;border-top:0 solid rgba(255,255,255,0);padding-top:14px;padding-bottom:14px;padding-left:30px;padding-right:30px;margin-left:0;margin-right:0;border-bottom:4px solid rgba(255,255,255,0);background:rgba(255,255,255,0)}.root::menuItem:isCurrentPage{border-left-style:solid;border-left-color:rgba(255,255,255,0);border-right-color:rgba(255,255,255,0);border-color:value(site_2_3);border-top-color:value(site_2_3);border-bottom-color:value(site_2_3);background:repeat padding-box border-box 0 / auto scroll linear-gradient(180deg,rgba(255,255,255,0) 0,rgba(255,255,255,0) 100%),rgba(117,117,117,0)}.root::menuItem:isHovered{border-color:value(site_2_2);border-bottom-color:rgba(24,24,24,0.3);border-right-color:value(site_2_2);border-top-color:rgba(24,24,24,0.3);background:repeat padding-box border-box 0 / auto scroll linear-gradient(180deg,rgba(255,255,255,0) 0,rgba(255,255,255,0) 100%),rgba(117,117,117,0)}.root::menuItem::label{letter-spacing:0;line-height:1.2em;color:value(site_1_5);-st-mixin:font_8;font-size:16px;font-family:madefor-text,helveticaneuew01-45ligh,helveticaneuew02-45ligh,helveticaneuew10-45ligh,sans-serif}.root::menuItem::container{justify-content:flex-start}.root::menuItem:isCurrentPage::label{text-decoration-line:none;color:value(site_1_5)}.root::menuItem:isHovered::label{text-decoration-line:none;color:value(site_1_5)}.root::rightAlignmentScrollItem{--flexGrow:1}.root::scrollButton{border:0 solid #757575;padding:10px;background:rgba(255,255,255,0.8);padding-top:0;padding-bottom:0}.root::scrollButton::icon{width:16px;height:16px;fill:value(site_1_5)}.root::scrollButton:hover::icon{fill:rgba(24,24,24,0.6)}.root::positionBox{margin-left:20px;margin-right:20px}.root::columnsLayout{--horizontalSpacing:28;padding:12px;border:1px solid #000;margin-top:0;--flyoutAlignment:left;border-bottom:1px solid value(site_1_5);background:value(site_1_1)}.root::columnsLayout::listWrapper{--width:80;--columnsAmount:3;width:100%;column-count:3}.root::columnsLayout::pageWrapper{justify-content:space-between;margin-bottom:-4px;margin-left:-14px;margin-right:-14px;width:calc(100%+28px)}.root::columnsLayout::pageStretchWrapper{width:980px}.root::columnsLayout::category{margin-top:4px}.root::columnsLayout::flex{margin-bottom:-8px}.root::columnsLayout::listItem{margin-left:14px;margin-right:14px}.root::columnsLayout::rowItem{margin-bottom:8px}.root::columnsLayout::menuItem{padding-right:10px;padding-left:10px;border:0 solid #757575;background:rgba(117,117,117,0);margin-bottom:4px;padding-top:6px;padding-bottom:6px}.root::columnsLayout::menuItem::container{justify-content:flex-start}.root::columnsLayout::menuItem::label{font-size:16px;font-family:"palatino linotype",serif;letter-spacing:0;color:value(site_1_5)}.root::columnsLayout::menuItem:isCurrentPage::label{text-decoration-line:underline}.root::columnsLayout::menuItem:isHovered::label{text-decoration-line:underline}.root::columnsLayout::heading{padding-left:10px;padding-right:10px;margin-bottom:4px;padding-top:6px;padding-bottom:6px;border:1px solid;border-width:0 0 3px;border-image:linear-gradient(58deg,rgba(237,88,41,0.76) 0,rgba(249,195,0,0.47) 13%,rgba(210,172,247,0.44) 36%,rgba(140,182,214,0.53) 48%,rgba(0,189,151,0.18) 66%,rgba(105,181,199,0.365) 76%,rgba(210,172,247,0.27) 86%,rgba(186,131,240,0.76) 99%) 1 / 0 0 3px stretch;border-radius:0}.root::columnsLayout::heading::container{justify-content:flex-start}.root::columnsLayout::heading::label{letter-spacing:.05em;font-style:italic;color:#000;font-weight:400;font-size:16px;font-family:"palatino linotype",serif}`;

const defaultStCssMobile = `:import{-st-from:'wix-ui-santa/index.st.css';-st-named:StylableHorizontalMenu}.root{-st-extends:StylableHorizontalMenu;border:0 solid #000;background:value(site_1_1);border-bottom:1px solid rgba(24,24,24,0.3)}.root::menu{direction:ltr;justify-content:flex-start;width:calc(100%+0);margin-left:-0;margin-right:-0;height:calc(100%+0);margin-top:-0;margin-bottom:-0;flex-grow:unset}.root::menuItem{border:0 solid #757575;background:rgba(117,117,117,0);margin-left:0;margin-right:0;margin-top:0;margin-bottom:0;border-bottom:4px solid rgba(255,255,255,0);padding-top:14px;padding-bottom:14px;padding-left:20px;padding-right:20px}.root::menuItem:isCurrentPage{}.root::menuItem:isHovered{}.root::menuItem::label{letter-spacing:0;text-align:left;color:value(site_1_5);-st-mixin:font_8;font-size:16px;font-family:madefor-text,helveticaneuew01-45ligh,helveticaneuew02-45ligh,helveticaneuew10-45ligh,sans-serif}.root::menuItem::container{justify-content:flex-start}.root::menuItem:isCurrentPage::label{text-decoration-line:none;color:value(site_2_3)}.root::menuItem:isHovered::label{text-decoration-line:none;color:value(site_1_5)}.root::rightAlignmentScrollItem{flex-grow:1;--flexGrow:unset}.root::scrollButton{border:0 solid #757575;padding:10px;background:rgba(255,255,255,0.8);padding-top:0;padding-bottom:0}.root::scrollButton::icon{width:16px;height:16px;fill:value(site_1_5)}.root::scrollButton:hover::icon{fill:rgba(24,24,24,0.6)}.root::positionBox{margin-left:20px;margin-right:20px}.root::columnsLayout{--flyoutAlignment:left;--horizontalSpacing:28;padding:12px;background:#FFF;border:1px solid #000;margin-top:0}.root::leftAlignmentScrollItem{--flexGrow:unset}.root::columnsLayout::listWrapper{--width:80;--columnsAmount:3;column-count:3;width:80}.root::columnsLayout::pageWrapper{margin-bottom:-4px;margin-left:-14px;margin-right:-14px;width:calc(100%+28px);justify-content:flex-start}.root::columnsLayout::pageStretchWrapper{width:980px}.root::columnsLayout::category{margin-top:4px}.root::columnsLayout::flex{margin-bottom:-8px}.root::columnsLayout::listItem{margin-left:14px;margin-right:14px}.root::columnsLayout::rowItem{margin-bottom:8px}.root::columnsLayout::menuItem{padding-right:10px;padding-left:10px;border:0 solid #757575;background:rgba(117,117,117,0);margin-bottom:4px;padding-top:6px;padding-bottom:6px}.root::columnsLayout::menuItem::container{justify-content:flex-start}.root::columnsLayout::menuItem::label{font-size:16px;font-family:"palatino linotype",serif;letter-spacing:0;color:value(site_1_5)}.root::columnsLayout::menuItem:isCurrentPage::label{text-decoration-line:underline}.root::columnsLayout::menuItem:isHovered::label{text-decoration-line:underline}.root::columnsLayout::heading{padding-left:10px;padding-right:10px;margin-bottom:4px;padding-top:6px;padding-bottom:6px;border:1px solid;border-width:0 0 3px;border-image:linear-gradient(58deg,rgba(237,88,41,0.76) 0,rgba(249,195,0,0.47) 13%,rgba(210,172,247,0.44) 36%,rgba(140,182,214,0.53) 48%,rgba(0,189,151,0.18) 66%,rgba(105,181,199,0.365) 76%,rgba(210,172,247,0.27) 86%,rgba(186,131,240,0.76) 99%) 1 / 0 0 3px stretch;border-radius:0}.root::columnsLayout::heading::container{justify-content:flex-start}.root::columnsLayout::heading::label{letter-spacing:.05em;font-style:italic;color:#000;font-weight:400;font-size:16px;font-family:"palatino linotype",serif}`;

// Stylable selectors
const Selectors = {
  nav: '.root',
  menu: '.root::menu',
  menuItem: '.root::menuItem',
  menuItemHovered: '.root::menuItem:isHovered',
  menuItemCurrentPage: '.root::menuItem:isCurrentPage',
  menuItemLabel: '.root::menuItem::label',
  menuItemLabelHovered: '.root::menuItem:isHovered::label',
  menuItemLabelCurrentPage: '.root::menuItem:isCurrentPage::label',
  menuItemContainer: '.root::menuItem::container',
  positionBox: '.root::positionBox',
  scrollButton: '.root::scrollButton',
  scrollButtonIcon: '.root::scrollButton::icon',
  scrollButtonIconHovered: '.root::scrollButton:hover::icon',
  submenu: '.root::columnsLayout',
  megaMenuContainer: '.root::megaMenuContainer',
  submenuFlex: '.root::columnsLayout::flex',
  submenuItem: '.root::columnsLayout::menuItem',
  submenuItemLabel: '.root::columnsLayout::menuItem::label',
  submenuItemContainer: '.root::columnsLayout::menuItem::container',
  submenuListItem: '.root::columnsLayout::listItem',
  submenuRowItem: '.root::columnsLayout::rowItem',
  submenuHeading: '.root::columnsLayout::heading',
  submenuHeadingLabel: '.root::columnsLayout::heading::label',
  submenuHeadingContainer: '.root::columnsLayout::heading::container',
  submenuCategory: '.root::columnsLayout::category',
  submenuColumnsList: '.root::columnsLayout::columnsList',
  submenuListWrapper: '.root::columnsLayout::listWrapper',
  submenuPageWrapper: '.root::columnsLayout::pageWrapper',
  submenuPageStretchWrapper: '.root::columnsLayout::pageStretchWrapper',
  containerPageStretchWrapper: '.root::columnsLayout::containerPageStretchWrapper',
  leftAlignmentScrollItem: '.root::leftAlignmentScrollItem',
  rightAlignmentScrollItem: '.root::rightAlignmentScrollItem',
  containerPositionBox: '.root::containerPositionBox',
} as const;

/* Stylable Properties */

const colorMaps = {
  color_11: 'site_1_1',
  color_12: 'site_1_2',
  color_13: 'site_1_3',
  color_14: 'site_1_4',
  color_15: 'site_1_5',
  color_16: 'site_2_1',
  color_17: 'site_2_2',
  color_18: 'site_2_3',
  color_19: 'site_2_4',
  color_20: 'site_2_5',
  color_21: 'site_3_1',
  color_22: 'site_3_2',
  color_23: 'site_3_3',
  color_24: 'site_3_4',
  color_25: 'site_3_5',
  color_26: 'site_4_1',
  color_27: 'site_4_2',
  color_28: 'site_4_3',
  color_29: 'site_4_4',
  color_30: 'site_4_5',
  color_31: 'site_5_1',
  color_32: 'site_5_2',
  color_33: 'site_5_3',
  color_34: 'site_5_4',
  color_35: 'site_5_5',
} as const;

const mobileVariantConfig = {
  id: 'MOBILE-VARIANT',
  type: 'variants',
  pageId: 'masterPage',
} as const;

const defaultTextColor = 'color_15';

// Script taken from https://github.com/wix-private/autopilot-scripts/blob/master/stylable-horizontal-menu/replace-old-menu-with-shm.js
// and modified to suit MA migration needs.

const getDefaultStCss = (isMobile: boolean) => {
  return isMobile ? defaultStCssMobile : defaultStCssDesktop;
};

const parseStCss = (stCss) => {
  return stCss
    .split('}')
    .map((block) => block.split('{'))
    .filter((block) => block.length > 1)
    .map(([k, v]) => [k.trim(), v.split(';').filter((v1) => v1)]);
};

const stringifyStCss = (stCss) => {
  return stCss.map(([k, v]) => [k, v.map((v1) => `${v1};`).join('')].join('{')).join('}') + '}';
};

const getThemeFonts = async (editorSDK: EditorSDK) => {
  const fontsMap = await editorSDK.theme.fonts.getMap(APP_TOKEN);

  const themeFonts: Record<string, string> = {};

  for (const key in fontsMap) {
    const { style, variant, weight, size, lineHeight, family, color } = fontsMap[key];
    const fontString = `${style} ${variant} ${weight} ${size}/${lineHeight} ${family} ${color}`;

    themeFonts[key] = fontString;
  }

  return themeFonts;
};

const getStCssUtils = (stCss) => {
  // StCss Utils

  const findInStCss = (selector, attribute) => {
    const selectorIndex = stCss.findIndex((st) => st[0] === selector);
    const attributeIndex = stCss[selectorIndex]?.[1].findIndex((att) => att.startsWith(attribute)) ?? -1;

    return [selectorIndex, attributeIndex];
  };

  const deleteFromStCss = (selector, attribute) => {
    const [selectorIndex, attributeIndex] = findInStCss(selector, attribute);
    if (attributeIndex < 0) {
      return;
    }
    stCss[selectorIndex][1].splice(attributeIndex, 1);
  };

  const getValFromStCss = (selector, attribute) => {
    const [selectorIndex, attributeIndex] = findInStCss(selector, attribute);
    if (attributeIndex < 0) {
      return;
    }
    return stCss[selectorIndex][1][attributeIndex].split(':')[1].trim();
  };

  const findAndReplaceStCss = (selector, attribute, val) => {
    const [selectorIndex, attributeIndex] = findInStCss(selector, attribute);
    if (selectorIndex < 0) {
      return;
    }

    const toAdd = `${attribute}: ${val}`;
    if (attributeIndex >= 0) {
      stCss[selectorIndex][1][attributeIndex] = toAdd;
    } else {
      stCss[selectorIndex][1].push(toAdd);
    }
  };

  return {
    findInStCss,
    getValFromStCss,
    findAndReplaceStCss,
    deleteFromStCss,
  };
};

/** *************************
    /***** Mapping Methods *****
    /***************************

    /**
     * Map properties from DDM to SHM
     * @param {'right'|'center'|'left'} alignButtons ,
     * @param {'right'|'center'|'left'} alignText,
     * @param {boolean} rtl,
     * @param {boolean} sameWidthButtons,
     * @param {boolean} stretchButtonsToMenuWidth
     */
const mapProps = (stCss, props) => {
  const { alignButtons, alignText, rtl, sameWidthButtons, stretchButtonsToMenuWidth } = props;

  const { getValFromStCss, findAndReplaceStCss } = getStCssUtils(stCss);

  const directionMap = rtl
    ? {
        left: 'flex-end',
        center: 'center',
        right: 'flex-start',
      }
    : {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
      };

  /**
   * Align Items Map By layout panel logic
   * {@link https://github.com/wix-private/editor-elements/blob/master/packages/editor-elements-library/src/components/StylableHorizontalMenu/editor/panels/layout/MenuTab/ItemsAlignmentController.tsx#L49 Github}
   */
  const mapAlignItems = () => {
    findAndReplaceStCss(Selectors.menu, 'justify-content', directionMap[alignButtons]);
    findAndReplaceStCss(Selectors.menu, 'direction', rtl ? 'rtl' : 'ltr');
    findAndReplaceStCss(Selectors.leftAlignmentScrollItem, 'flex-grow', alignButtons === 'left' ? 'unset' : '1');
    findAndReplaceStCss(Selectors.rightAlignmentScrollItem, 'flex-grow', alignButtons === 'right' ? 'unset' : '1');
  };

  // /**
  //  * Align Text Map By layout panel logic
  //  * {@link https://github.com/wix-private/editor-elements/blob/master/packages/editor-elements-library/src/components/StylableHorizontalMenu/editor/panels/layout/MenuTab/TextAlignmentController.tsx#L22 Github}
  //  */
  const mapAlignText = () => {
    findAndReplaceStCss(Selectors.menuItemContainer, 'justify-content', directionMap[alignText]);
  };

  /**
   *
   * Stretch Items Map By layout panel logic
   * {@link https://github.com/wix-private/editor-elements/blob/master/packages/editor-elements-library/src/components/StylableHorizontalMenu/editor/panels/layout/MenuTab/ItemsFluidController.tsx#L23 Github}
   */
  const mapStretchItems = () => {
    findAndReplaceStCss(Selectors.menu, 'flex-grow', stretchButtonsToMenuWidth ? '1' : 'unset');
    findAndReplaceStCss(
      Selectors.leftAlignmentScrollItem,
      'flex-grow',
      stretchButtonsToMenuWidth ? 'unset' : getValFromStCss(Selectors.leftAlignmentScrollItem, '--flexGrow'),
    );
    findAndReplaceStCss(
      Selectors.leftAlignmentScrollItem,
      '--flexGrow',
      stretchButtonsToMenuWidth ? getValFromStCss(Selectors.leftAlignmentScrollItem, 'flex-grow') : 'unset',
    );
    findAndReplaceStCss(
      Selectors.rightAlignmentScrollItem,
      'flex-grow',
      stretchButtonsToMenuWidth ? 'unset' : getValFromStCss(Selectors.rightAlignmentScrollItem, '--flexGrow'),
    );
    findAndReplaceStCss(
      Selectors.rightAlignmentScrollItem,
      '--flexGrow',
      stretchButtonsToMenuWidth ? getValFromStCss(Selectors.rightAlignmentScrollItem, 'flex-grow') : 'unset',
    );
  };

  // Run
  mapAlignItems();
  mapAlignText();
  mapStretchItems();
};

// style mappings

const skinsWithFixedInsetShd = new Set([
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateIndentedMenuButtonSkin',
]);

const skinsWithMenuItemsSeparate = new Set([
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateBasicMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateShinyIMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateShinyIIMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateLinesMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateIndentedMenuButtonSkin',
]);

const skinsWithFullBorderSettings = new Set([
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SolidColorMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateBasicMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.LinesMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateLinesMenuButtonSkin',
]);

const skinsWithOnlyVerticalBorders = new Set([
  'wysiwyg.common.components.dropdownmenu.viewer.skins.LinesMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateLinesMenuButtonSkin',
]);

const skinsWithoutShadow = new Set([
  'wysiwyg.common.components.dropdownmenu.viewer.skins.TextOnlyMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.TextSeparatorsMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.OverlineMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.LinesMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SeparateLinesMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.PointerMenuButtonSkin',
  'wysiwyg.common.components.dropdownmenu.viewer.skins.SloppyBorderMenuButtonSkin',
]);

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex?.slice(1, 3), 16);
  const g = parseInt(hex?.slice(3, 5), 16);
  const b = parseInt(hex?.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbToRgba = (rgb, alpha) => {
  const [r, g, b] = rgb
    ?.slice(4, -1)
    .split(',')
    .map((val) => parseInt(val.trim(), 10));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isHex = (val) => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(val);
};

const isRgb = (val) => {
  return /^rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)$/.test(val);
};

const parsedStyleForFont = (cssString) => {
  const [fontStyle, fontVariant, fontWeight, fontSizeLineHeight, fontFamily, color] = cssString.split(' ');

  return {
    fontStyle,
    fontVariant,
    fontWeight,
    fontFamily,
    fontSize: fontSizeLineHeight.split('/')[0],
    lineHeight: fontSizeLineHeight.split('/')?.[1],
    color,
  }; // CSSStyleDeclaration object
};

const getStyleMapperUtils = async ({
  editorSDK,
  stCss,
  oldSkin,
  oldStyle,
  oldLayout,
  isMobile,
}: StyleMapperUtilsGetterParams) => {
  const { properties, propertiesOverride } = oldStyle;

  const { findAndReplaceStCss, deleteFromStCss } = getStCssUtils(stCss);

  const colors = await editorSDK.theme.colors.getAll(APP_TOKEN);

  const themeFonts = await getThemeFonts(editorSDK);

  const menuItemsSeparate = skinsWithMenuItemsSeparate.has(oldSkin);

  const noBrdX = skinsWithOnlyVerticalBorders.has(oldSkin);

  const hasShadow = !skinsWithoutShadow.has(oldSkin);

  const withFixedShd = skinsWithFixedInsetShd.has(oldSkin);

  const shouldRetainDefaultBorder = !skinsWithFullBorderSettings.has(oldSkin) || !parseInt(properties.brw, 10);

  const convertThemeVal = (val) => {
    if (typeof val === 'number') {
      return val;
    }
    if (val?.startsWith('color')) {
      return `value(${colorMaps[val] ?? val})`;
    }
    if (val?.startsWith('font')) {
      return `value(${properties.fnt})`;
    }
    return val;
  };

  const replaceFont = (selector, { fontSize, fontFamily, fontStyle, fontWeight, lineHeight, color }) => {
    if (fontSize) {
      findAndReplaceStCss(selector, 'font-size', fontSize);
    }
    if (fontFamily) {
      findAndReplaceStCss(selector, 'font-family', fontFamily);
    }
    if (fontStyle) {
      findAndReplaceStCss(selector, 'font-style', fontStyle);
    }
    if (fontWeight) {
      findAndReplaceStCss(selector, 'font-weight', fontWeight);
    }
    if (lineHeight) {
      findAndReplaceStCss(selector, 'line-height', lineHeight);
    }
  };

  const getColorFromTheme = (colorProp, alphaProp) => {
    let color = properties[colorProp];
    const alpha = properties[alphaProp] ?? '1';

    if (alpha !== '1') {
      if (color?.startsWith('color')) {
        color = colors[color];
      }
      if (isHex(color)) {
        color = hexToRgba(color, alpha);
      } else if (isRgb(color)) {
        color = rgbToRgba(color, alpha);
      }
    } else {
      color = convertThemeVal(color);
    }

    return color;
  };

  return {
    isMobile,
    oldLayout,
    oldSkin,
    stCss,
    properties,
    propertiesOverride,
    colors,
    themeFonts,
    menuItemsSeparate,
    noBrdX,
    hasShadow,
    withFixedShd,
    shouldRetainDefaultBorder,
    findAndReplaceStCss,
    deleteFromStCss,
    convertThemeVal,
    replaceFont,
    getColorFromTheme,
  };
};

const mapBgColor = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, menuItemsSeparate, getColorFromTheme, findAndReplaceStCss } = styleMapperUtils;

  if (properties.bg) {
    const newBgColor = getColorFromTheme('bg', 'alpha-bg');
    const selector = menuItemsSeparate ? Selectors.menuItem : Selectors.nav;
    findAndReplaceStCss(selector, 'background', newBgColor);
  }

  if (properties.bgh) {
    const newHoveredBgColor = getColorFromTheme('bgh', 'alpha-bgh');
    findAndReplaceStCss(Selectors.menuItemHovered, 'background', newHoveredBgColor);
  }

  if (properties.bgs) {
    const newSelectedBgColor = getColorFromTheme('bgs', 'alpha-bgs');
    findAndReplaceStCss(Selectors.menuItemCurrentPage, 'background', newSelectedBgColor);
  }
};

const mapSeparatorStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, getColorFromTheme, findAndReplaceStCss, deleteFromStCss } = styleMapperUtils;

  if (properties.sep) {
    const newSepColor = getColorFromTheme('sep', 'alpha-sep');
    findAndReplaceStCss(Selectors.menuItem, 'border-left', `1px solid ${newSepColor}`);
    findAndReplaceStCss(Selectors.menuItemHovered, 'border-left-color', newSepColor);
    // set border-left-color at the end to properly overwrite default value
    deleteFromStCss(Selectors.menuItemCurrentPage, 'border-left-color');
    findAndReplaceStCss(Selectors.menuItemCurrentPage, 'border-left-color', newSepColor);
  }
};

const mapSubmenuBg = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, getColorFromTheme, findAndReplaceStCss } = styleMapperUtils;

  if (properties.bgDrop) {
    const newSubmenuBgColor = getColorFromTheme('bgDrop', 'alpha-bgDrop');
    findAndReplaceStCss(Selectors.submenu, 'background', newSubmenuBgColor);
  }
};

const mapBorderRadius = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, shouldRetainDefaultBorder, menuItemsSeparate, findAndReplaceStCss } = styleMapperUtils;

  if (properties.rd && !shouldRetainDefaultBorder) {
    const selector = menuItemsSeparate ? Selectors.menuItem : Selectors.nav;
    findAndReplaceStCss(selector, 'border-radius', properties.rd);
  }
};

const mapSubmenuBorderRadius = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, shouldRetainDefaultBorder, findAndReplaceStCss } = styleMapperUtils;

  if (properties.rdDrop && !shouldRetainDefaultBorder) {
    findAndReplaceStCss(Selectors.submenu, 'border-radius', properties.rdDrop);
  }
};

const mapBorderStyles = (styleMapperUtils: StyleMapperUtils) => {
  const {
    properties,
    shouldRetainDefaultBorder,
    menuItemsSeparate,
    noBrdX,
    getColorFromTheme,
    findAndReplaceStCss,
    deleteFromStCss,
  } = styleMapperUtils;

  if (!shouldRetainDefaultBorder) {
    const { brw } = properties;
    const selector = menuItemsSeparate ? Selectors.menuItem : Selectors.nav;
    const brdColor = getColorFromTheme('brd', 'alpha-brd');

    if (noBrdX) {
      findAndReplaceStCss(selector, 'border-top', `${brw}px solid ${brdColor}`);
      findAndReplaceStCss(selector, 'border-bottom', `${brw}px solid ${brdColor}`);
    } else {
      findAndReplaceStCss(selector, 'border', `${brw}px solid ${brdColor}`);
      deleteFromStCss(selector, 'border-top');
      deleteFromStCss(selector, 'border-bottom');
    }

    if (menuItemsSeparate) {
      deleteFromStCss(Selectors.nav, 'border-bottom');
    }
  }
};

const mapShadowStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, hasShadow, menuItemsSeparate, withFixedShd, convertThemeVal, findAndReplaceStCss } =
    styleMapperUtils;

  if (!hasShadow || properties['boxShadowToggleOn-shd'] !== 'true') {
    return;
  }

  const { shd } = properties;

  let menuShadow = shd;
  let submenuShd = shd;

  if (!shd.includes('rgba') || !shd.includes('rgb')) {
    const shdArr = shd.split(' ');
    // Convert color
    shdArr[shdArr.length - 1] = convertThemeVal(shdArr[shdArr.length - 1]);
    menuShadow = submenuShd = shdArr.join(' ');
  }

  if (withFixedShd) {
    const fixedInsetShdValue = ',inset -0.09px -3px 21px -10px #000000, 0.68px 1.88px 3px rgb(0 0 0 / 56%)';
    menuShadow += fixedInsetShdValue;
  }

  const selector = menuItemsSeparate ? Selectors.menuItem : Selectors.nav;
  findAndReplaceStCss(selector, 'box-shadow', menuShadow);
  findAndReplaceStCss(Selectors.submenu, 'box-shadow', submenuShd);
};

const mapSubmenuStyles = (styleMapperUtils: StyleMapperUtils) => {
  mapSubmenuBg(styleMapperUtils);
  mapSubmenuBorderRadius(styleMapperUtils);
};

const mapScrollButtonStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { colors, properties, convertThemeVal, findAndReplaceStCss } = styleMapperUtils;
  const { bg, txt = defaultTextColor } = properties;

  // background
  const bgAlpha = 0.8;
  const bgColorHex = bg?.startsWith('color') ? colors[bg] : isHex(bg) ? bg : colors.color_11;
  const bgColorRgba = hexToRgba(bgColorHex, bgAlpha);

  // regular state arrow
  const arrowColor = convertThemeVal(txt);

  // hovered state arrow
  const hoveredArrowAlpha = 0.6;
  const arrowColorHex = txt.startsWith('color') ? colors[txt] : isHex(txt) ? txt : colors.color_15;
  const hoveredArrowColorRgba = hexToRgba(arrowColorHex, hoveredArrowAlpha);

  findAndReplaceStCss(Selectors.scrollButton, 'background', bgColorRgba);
  findAndReplaceStCss(Selectors.scrollButtonIcon, 'fill', arrowColor);
  findAndReplaceStCss(Selectors.scrollButtonIconHovered, 'fill', hoveredArrowColorRgba);
};

const mapRegularStateStyles = (styleMapperUtils: StyleMapperUtils) => {
  mapSeparatorStyles(styleMapperUtils);
  mapBorderRadius(styleMapperUtils);
  mapBorderStyles(styleMapperUtils);
  mapShadowStyles(styleMapperUtils);
};

const mapHoveredStateBorderStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, shouldRetainDefaultBorder, getColorFromTheme, findAndReplaceStCss, deleteFromStCss } =
    styleMapperUtils;

  if (properties.brdh && !shouldRetainDefaultBorder) {
    const newBrdhColor = getColorFromTheme('brdh', 'alpha-brdh');
    findAndReplaceStCss(Selectors.menuItemHovered, 'border-color', newBrdhColor);
    deleteFromStCss(Selectors.menuItemHovered, 'border-bottom-color');
    deleteFromStCss(Selectors.menuItemHovered, 'border-right-color');
    deleteFromStCss(Selectors.menuItemHovered, 'border-top-color');
  }
};

const mapHoveredStateBgStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, getColorFromTheme, findAndReplaceStCss } = styleMapperUtils;

  if (properties.bgh) {
    const newBghColor = getColorFromTheme('bgh', 'alpha-bgh');
    findAndReplaceStCss(Selectors.menuItemHovered, 'background', newBghColor);
  }
};

const mapHoveredStateStyles = (styleMapperUtils: StyleMapperUtils) => {
  mapHoveredStateBorderStyles(styleMapperUtils);
  mapHoveredStateBgStyles(styleMapperUtils);
};

const mapSelectedStateBorderStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, shouldRetainDefaultBorder, getColorFromTheme, findAndReplaceStCss, deleteFromStCss } =
    styleMapperUtils;

  if (properties.brds && !shouldRetainDefaultBorder) {
    const newBrdsColor = getColorFromTheme('brds', 'alpha-brds');
    findAndReplaceStCss(Selectors.menuItemCurrentPage, 'border-color', newBrdsColor);

    deleteFromStCss(Selectors.menuItemCurrentPage, 'border-bottom-color');
    deleteFromStCss(Selectors.menuItemCurrentPage, 'border-right-color');
    deleteFromStCss(Selectors.menuItemCurrentPage, 'border-top-color');
    deleteFromStCss(Selectors.menuItemCurrentPage, 'border-left-color');
  }
};

const mapSelectedStateBgStyles = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, getColorFromTheme, findAndReplaceStCss } = styleMapperUtils;

  if (properties.bgs) {
    const newBgsColor = getColorFromTheme('bgs', 'alpha-bgs');
    findAndReplaceStCss(Selectors.menuItemCurrentPage, 'background', newBgsColor);
  }
};

const mapSelectedStateStyles = (styleMapperUtils: StyleMapperUtils) => {
  mapSelectedStateBorderStyles(styleMapperUtils);
  mapSelectedStateBgStyles(styleMapperUtils);
};

const getFontData = ({
  styleMapperUtils,
  overrideProp,
  initialFontValue,
}: {
  styleMapperUtils: StyleMapperUtils;
  overrideProp: 'fnt' | 'fntDrop';
  initialFontValue: string;
}) => {
  const { isMobile, oldLayout, themeFonts, propertiesOverride } = styleMapperUtils;

  let fontValue = initialFontValue;

  if (fontValue.startsWith('font')) {
    fontValue = themeFonts[fontValue];
  }

  const parsedFont = parsedStyleForFont(fontValue);
  const parsedFontOverride = {
    ...propertiesOverride?.[overrideProp],
  };

  if (isMobile) {
    const scale = oldLayout.scale ?? 1;
    const parsedFontSize = parseIntFromPx(parsedFontOverride.fontSize ?? parsedFont.fontSize);

    parsedFontOverride.fontSize = convertFontSizeToMobile(parsedFontSize, scale) + 'px';
  }

  return {
    ...parsedFont,
    ...parsedFontOverride,
  };
};

const mapFont = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, replaceFont } = styleMapperUtils;

  const menuFontValue: string = properties.fnt;
  const submenuFontValue: string = properties.fntDrop ?? menuFontValue;

  const menuFontData = getFontData({
    styleMapperUtils,
    overrideProp: 'fnt',
    initialFontValue: menuFontValue,
  });

  const submenuFontData = getFontData({
    styleMapperUtils,
    overrideProp: 'fntDrop',
    initialFontValue: submenuFontValue,
  });

  replaceFont(Selectors.menuItemLabel, menuFontData);
  replaceFont(Selectors.submenuItemLabel, submenuFontData);
};

const mapTextColors = (styleMapperUtils: StyleMapperUtils) => {
  const { properties, findAndReplaceStCss, convertThemeVal } = styleMapperUtils;

  const textColor = convertThemeVal(properties.txt ?? defaultTextColor);
  findAndReplaceStCss(Selectors.menuItemLabel, 'color', textColor);
  findAndReplaceStCss(Selectors.submenuItemLabel, 'color', textColor);

  if (properties.txth) {
    const hoveredTextColor = convertThemeVal(properties.txth);
    findAndReplaceStCss(Selectors.menuItemLabelHovered, 'color', hoveredTextColor);
  }

  if (properties.txts) {
    const selectedTextColor = convertThemeVal(properties.txts);
    findAndReplaceStCss(Selectors.menuItemLabelCurrentPage, 'color', selectedTextColor);
  }
};

const mapStyles = (styleMapperUtils: StyleMapperUtils) => {
  mapBgColor(styleMapperUtils);
  mapSubmenuStyles(styleMapperUtils);
  mapScrollButtonStyles(styleMapperUtils);
  mapFont(styleMapperUtils);
  mapTextColors(styleMapperUtils);
  mapRegularStateStyles(styleMapperUtils);
  mapHoveredStateStyles(styleMapperUtils);
  mapSelectedStateStyles(styleMapperUtils);
};

const getRootStyleData = (isMobile: boolean, newMemberMenuComponentData: NewMemberMenuComponentData) => {
  return isMobile ? newMemberMenuComponentData.scopedStyles['MOBILE-VARIANT'] : newMemberMenuComponentData.style;
};

const getFinalizedMenuRef = (isMobile: boolean, componentRef: ComponentRef) => {
  return {
    ...componentRef,
    ...(isMobile && {
      variants: [mobileVariantConfig],
    }),
  };
};

export const migrateHorizontalMemberMenuComponent = async ({
  editorSDK,
  isMobile,
  migrationData,
}: HorizontalMenuMigrationParams) => {
  const { oldMemberMenuComponentSettings, newMemberMenuComponentData } = migrationData;
  const { props: oldProps, style: oldStyle, layout: oldLayout } = oldMemberMenuComponentSettings;
  const stCss = parseStCss(getDefaultStCss(isMobile));

  if (!isMobile) {
    mapProps(stCss, oldProps);
  }

  const styleMapperUtils = await getStyleMapperUtils({
    editorSDK,
    stCss,
    oldSkin: oldStyle.skin,
    oldStyle: oldStyle.style,
    oldLayout,
    isMobile,
  });

  mapStyles(styleMapperUtils);

  const stCssStringified = stringifyStCss(stCss);
  const rootStyleData = getRootStyleData(isMobile, newMemberMenuComponentData);
  const componentRef = getFinalizedMenuRef(isMobile, newMemberMenuComponentData.componentRef);

  return editorSDK.components.style.updateFull(APP_TOKEN, {
    componentRef,
    style: {
      ...rootStyleData,
      style: {
        ...rootStyleData.style,
        properties: {
          '$st-css': stCssStringified,
        },
      },
    },
  });
};
